import type { Edge, Node, Scene } from '../../../core/models/scene';
import type { OrgModelProjection } from '../models/OrgModelProjection';
import {
  POSITION_STAFFING_CARD_HEIGHT,
  POSITION_STAFFING_CARD_WIDTH,
  positionStaffingAccent,
} from '../components/positionStaffingCard';
import {
  DEFAULT_POSITION_CHART_GROUPING_POLICY,
  type PositionChartGroupingPolicy,
} from '../models/PositionChartGrouping';
import { computeFullTreeLayout } from './graphAnalysis';
import { projectPositionChartGroups } from './positionChartGrouping';

/**
 * Builds a locked canvas projection. Canonical facts never enter the diagram Y.Doc.
 * Occupancy-only refreshes reuse the previous topology coordinates and skip tree layout.
 */
export function buildPositionChartScene(
  model: OrgModelProjection,
  previous?: Scene,
  options: {
    skipLayout?: boolean;
    groupingPolicy?: PositionChartGroupingPolicy;
  } = {},
): Scene {
  const grouping = projectPositionChartGroups(
    model,
    options.groupingPolicy ?? DEFAULT_POSITION_CHART_GROUPING_POLICY,
  );
  const nodes: Node[] = grouping.groups.map((group) => {
    const occupancyVisibility = group.occupancyVisibility;
    const staffingStatus = occupancyVisibility === 'visible' ? group.staffingStatus : 'unknown';
    const occupantAttrs = occupancyVisibility === 'visible'
      ? {
        occupants: group.occupants,
        staffedCount: group.staffedCount,
        vacantCount: group.vacantCount,
      }
      : {};
    const singleton = group.positions.length === 1 ? group.positions[0] : null;
    return {
      id: group.id,
      type: 'org_position',
      version: 2,
      category: 'orgchart',
      x: 0,
      y: 0,
      width: POSITION_STAFFING_CARD_WIDTH,
      height: POSITION_STAFFING_CARD_HEIGHT,
      w: POSITION_STAFFING_CARD_WIDTH,
      h: POSITION_STAFFING_CARD_HEIGHT,
      name: group.label,
      text: group.label,
      locked: true,
      lockOwner: 'projection',
      attrs: {
        __shapeVersion: 2,
        ...(singleton ? { positionId: singleton.externalId ?? singleton.id } : {}),
        // Succession drafting gates on the canonical record UUID
        // (SuccessionPlanningSection reads attrs.folderRecordId).
        ...(singleton ? { folderRecordId: singleton.id } : {}),
        positionGroup: group.positionIds.length > 1,
        positionIds: group.positionIds,
        approvedCount: group.approvedCount,
        title: group.label,
        department: group.department ?? undefined,
        location: group.location ?? undefined,
        level: group.level ?? undefined,
        occupancyVisibility,
        staffingStatus,
        ...occupantAttrs,
        ...(model.completeness?.relationships.complete === true
          ? { successorCount: group.successorCount }
          : {}),
        sourceStatus: singleton?.provenance.sourceBinding?.status ?? singleton?.provenance.source?.status ?? null,
        accentColor: positionStaffingAccent(staffingStatus),
      },
    } as Node;
  });
  const edges: Edge[] = grouping.edges.map((edge) => ({
      id: edge.id,
      type: 'reporting',
      from: edge.from,
      to: edge.to,
      attrs: { relation: 'reporting', predicate: 'reports_to', locked: true },
    }));
  if (previous && positionChartTopologyMatches(previous, nodes, edges)) {
    const previousNodes = new Map(previous.nodes.map((node) => [node.id, node]));
    for (const node of nodes) {
      const prior = previousNodes.get(node.id);
      if (prior) Object.assign(node, { x: prior.x, y: prior.y });
    }
  } else if (!options.skipLayout) {
    const positions = computeFullTreeLayout(nodes, edges);
    applyPositionChartLayoutPositions(nodes, positions);
  }
  const now = new Date().toISOString();
  return {
    version: 1,
    nodes,
    edges,
    meta: { createdAt: previous?.meta?.createdAt ?? now, updatedAt: now },
  };
}

export function applyPositionChartLayoutPositions(
  nodes: Node[],
  positions: ReadonlyMap<string, { x: number; y: number }>,
): void {
  for (const node of nodes) {
    const point = positions.get(node.id);
    if (point) Object.assign(node, point);
  }
}

export function positionChartTopologyMatches(
  previous: Scene,
  nodes: readonly Node[],
  edges: readonly Edge[],
): boolean {
  if (previous.nodes.length !== nodes.length || previous.edges.length !== edges.length) return false;
  const previousNodeIds = new Set(previous.nodes.map((node) => node.id));
  if (nodes.some((node) => !previousNodeIds.has(node.id))) return false;
  const previousEdges = new Map(previous.edges.map((edge) => [edge.id, `${edge.from}\u0000${edge.to}`]));
  return edges.every((edge) => previousEdges.get(edge.id) === `${edge.from}\u0000${edge.to}`);
}

/** Produces stable-ID patches so occupancy refreshes do not replace unaffected cards. */
export function positionChartSceneNodeUpdates(
  previous: Scene,
  next: Scene,
): Array<{ id: string; updates: Partial<Node> }> {
  const previousNodes = new Map(previous.nodes.map((node) => [node.id, node]));
  return next.nodes.flatMap((node) => {
    const prior = previousNodes.get(node.id);
    if (prior && sameProjectedNode(prior, node)) return [];
    return [{ id: node.id, updates: node }];
  });
}

function sameProjectedNode(left: Node, right: Node): boolean {
  return left.x === right.x
    && left.y === right.y
    && left.width === right.width
    && left.height === right.height
    && left.name === right.name
    && left.text === right.text
    && JSON.stringify(left.attrs ?? {}) === JSON.stringify(right.attrs ?? {});
}
